import React from 'react'
import NewsletterSection from '../components/sections/NewsletterSection'

const NewsletterDemoPage: React.FC = () => {
  return (
    <div className="min-h-screen">
      {/* Page Header */}
      <div className="bg-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            Newsletter Signup Section
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed mb-8">
            Professional newsletter signup form with Hudson Club branding. Features responsive design, 
            form validation, and elegant animations based on exact Figma specifications.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Clean Form Design</h3>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Two-field layout (Name + Email)</li>
                <li>• 540px section height</li>
                <li>• Professional input styling</li>
                <li>• Hudson Club brand colors</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">User Experience</h3>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Form validation included</li>
                <li>• Smooth animations</li>
                <li>• Mobile-responsive layout</li>
                <li>• Success state ready</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Integration Ready</h3>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Email marketing platform ready</li>
                <li>• Lead capture optimized</li>
                <li>• Privacy-focused messaging</li>
                <li>• Conversion-focused design</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Main Component Showcase */}
      <NewsletterSection />

      {/* Implementation Guide */}
      <div className="bg-gray-50 py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Implementation & Integration Guide
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            
            {/* Technical Specifications */}
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                🎨 Design Specifications
              </h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="font-medium text-gray-700">Section Height</span>
                  <span className="text-gray-600">540px</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="font-medium text-gray-700">Background</span>
                  <span className="text-gray-600">rgb(246, 246, 241)</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="font-medium text-gray-700">Text Color</span>
                  <span className="text-gray-600">rgb(30, 29, 28)</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="font-medium text-gray-700">Button Color</span>
                  <span className="text-gray-600">rgb(82, 76, 61)</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="font-medium text-gray-700">Input Size</span>
                  <span className="text-gray-600">180px × 38px</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="font-medium text-gray-700">Button Size</span>
                  <span className="text-gray-600">97px × 38px</span>
                </div>
              </div>
            </div>

            {/* Features & Functionality */}
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                ⚡ Features & Functionality
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-3 h-3 rounded-full bg-blue-500 mr-3 mt-1 flex-shrink-0"></div>
                  <div>
                    <p className="font-medium text-gray-900">Form Validation</p>
                    <p className="text-gray-600 text-sm">Built-in HTML5 validation with required fields</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-3 h-3 rounded-full bg-green-500 mr-3 mt-1 flex-shrink-0"></div>
                  <div>
                    <p className="font-medium text-gray-900">State Management</p>
                    <p className="text-gray-600 text-sm">React useState for form data handling</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-3 h-3 rounded-full bg-purple-500 mr-3 mt-1 flex-shrink-0"></div>
                  <div>
                    <p className="font-medium text-gray-900">Smooth Animations</p>
                    <p className="text-gray-600 text-sm">Framer Motion entrance effects</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-3 h-3 rounded-full bg-yellow-500 mr-3 mt-1 flex-shrink-0"></div>
                  <div>
                    <p className="font-medium text-gray-900">Mobile Responsive</p>
                    <p className="text-gray-600 text-sm">Dedicated mobile layout optimization</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-3 h-3 rounded-full bg-red-500 mr-3 mt-1 flex-shrink-0"></div>
                  <div>
                    <p className="font-medium text-gray-900">Success States</p>
                    <p className="text-gray-600 text-sm">Ready-to-use confirmation messaging</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Integration Examples */}
          <div className="bg-white rounded-lg p-8 shadow-sm mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              🔗 Email Platform Integration
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div className="text-center p-6 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-blue-600 text-xl">📧</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Mailchimp</h4>
                <p className="text-gray-600 text-sm">Connect to Mailchimp API for automated list management</p>
              </div>
              
              <div className="text-center p-6 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-green-600 text-xl">📨</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">ConvertKit</h4>
                <p className="text-gray-600 text-sm">Integrate with ConvertKit forms and sequences</p>
              </div>
              
              <div className="text-center p-6 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-purple-600 text-xl">✉️</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Custom API</h4>
                <p className="text-gray-600 text-sm">Connect to your own backend API endpoint</p>
              </div>
            </div>

            <div className="bg-gray-900 rounded-lg p-6 text-white">
              <h4 className="text-lg font-semibold mb-4 text-gray-300">Sample Integration Code:</h4>
              <pre className="text-sm overflow-x-auto">
                <code>{`const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault()
  
  try {
    const response = await fetch('/api/newsletter-signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    })
    
    if (response.ok) {
      // Show success state
      setShowSuccess(true)
      setFormData({ fullName: '', email: '' })
    }
  } catch (error) {
    console.error('Signup error:', error)
  }
}`}</code>
              </pre>
            </div>
          </div>

          {/* Usage Scenarios */}
          <div className="bg-white rounded-lg p-8 shadow-sm">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              🎯 Perfect Placements
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-blue-600 text-xl">🏠</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Website Footer</h4>
                <p className="text-gray-600 text-sm">Above footer for maximum visibility</p>
              </div>
              
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-green-600 text-xl">📄</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Landing Pages</h4>
                <p className="text-gray-600 text-sm">Lead magnets and conversion pages</p>
              </div>
              
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-purple-600 text-xl">📖</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Blog Sidebar</h4>
                <p className="text-gray-600 text-sm">Content-based lead capture</p>
              </div>
              
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-yellow-600 text-xl">🎯</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Exit Intent</h4>
                <p className="text-gray-600 text-sm">Modal popups for leaving visitors</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default NewsletterDemoPage 